(() => {
  const rootSelector = '[data-bp-beautyves-concierge]';
  const Core = window.BPBeautyVesConciergeCore;
  if (!Core) return;

  if (window.BPBeautyVesConcierge) {
    window.BPBeautyVesConcierge.initialize(document);
    return;
  }

  const instances = new WeakMap();
  const instancesBySectionId = new Map();

  class BeautyVesConcierge {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.liveRegion = root.querySelector('[data-bp-beautyves-live]');
      this.pathQuestion = root.querySelector('[data-bp-beautyves-path-question]');
      this.resultQuestion = root.querySelector('[data-bp-beautyves-result-question]');
      this.results = Array.from(root.querySelectorAll('[data-bp-beautyves-result]'));
      this.gate = Core.createSubmissionGate();
      this.abortController = new AbortController();
      this.openEmitted = false;
      this.selectedPath = '';
      this.selectedResultKey = '';
      this.step = 1;
      this.atelier = root.hasAttribute('data-bv-atelier');
      this.handleChange = this.handleChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
    }

    initialize() {
      const { signal } = this.abortController;
      this.root.classList.add('is-enhanced');
      this.root.addEventListener('change', this.handleChange, { signal });
      this.root.addEventListener('click', this.handleClick, { signal });
      this.results.forEach((result) => {
        result.hidden = true;
        this.updateRoutineState(result);
      });
      if (this.resultQuestion) this.resultQuestion.hidden = true;
      this.root.querySelectorAll('[data-bp-beautyves-path], [data-bp-beautyves-result-choice]').forEach(input => { input.checked = false; });
      this.showStep(1);
      this.emitOpen();
    }

    emitOpen() {
      if (this.openEmitted) return;
      this.openEmitted = true;
      this.emit('bp:beautyves_concierge_open', { source: 'beautyves-concierge' });
    }

    handleChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.matches('[data-bp-beautyves-path]')) {
        this.selectPath(target.value, { source: 'customer' });
        return;
      }

      if (target.matches('[data-bp-beautyves-result-choice]')) {
        this.selectResult(target.value, { source: 'customer' });
        this.emit('bp:beautyves_answer', {
          path: this.selectedPath,
          result_key: target.value,
          source: 'beautyves-concierge',
        });
        return;
      }

      if (target.matches('[data-bp-beautyves-variant]')) {
        const card = target.closest('[data-bp-beautyves-product]');
        card.dataset.selectedVariantId = target.value;
        card.dataset.selectedVariantPrice = target.dataset.variantPrice || '';
        card.dataset.selectedVariantAvailable = target.dataset.variantAvailable || 'false';
        card.querySelector('[data-bp-beautyves-variant-error]')?.setAttribute('hidden', '');
        this.updateRoutineState(target.closest('[data-bp-beautyves-result]'));
      }
    }

    handleClick(event) {
      const navigation = event.target.closest('[data-bv-go], [data-bv-next]');
      if (navigation && this.root.contains(navigation)) {
        this.showStep(navigation.hasAttribute('data-bv-next') ? this.step + 1 : Number(navigation.dataset.bvGo), true);
        return;
      }
      const fallback = event.target.closest('[data-bp-beautyves-fallback]');
      if (fallback && this.root.contains(fallback)) {
        const resultKey = fallback.dataset.fallbackResultKey;
        const choice = this.root.querySelector(`[data-bp-beautyves-result-choice][value="${CSS.escape(resultKey)}"]`);
        if (choice) {
          choice.checked = true;
          this.selectResult(resultKey, { source: 'fallback', focus: true });
          this.showStep(3, true);
        }
        return;
      }

      const singleButton = event.target.closest('[data-bp-beautyves-add-single]');
      if (singleButton && this.root.contains(singleButton)) {
        const card = singleButton.closest('[data-bp-beautyves-product]');
        this.addItems([card], singleButton, 'single');
        return;
      }

      const addAllButton = event.target.closest('[data-bp-beautyves-add-all]');
      if (addAllButton && this.root.contains(addAllButton)) {
        const result = addAllButton.closest('[data-bp-beautyves-result]');
        this.addItems(Array.from(result.querySelectorAll('[data-bp-beautyves-product]')), addAllButton, 'routine');
      }
    }

    selectPath(path, options = {}) {
      if (!Core.PATHS.includes(path)) return;
      const changed = this.selectedPath !== path;
      this.selectedPath = path;
      if (changed) this.selectedResultKey = '';
      this.root.querySelectorAll('[data-bp-beautyves-result-option]').forEach((option) => {
        const matches = option.dataset.resultPath === path;
        option.hidden = !matches;
        option.querySelector('input').disabled = !matches;
        if (!matches || changed) option.querySelector('input').checked = false;
      });
      this.results.forEach((result) => { result.hidden = true; });
      if (this.resultQuestion) this.resultQuestion.hidden = false;
      if (this.atelier) this.showStep(this.step === 3 ? 2 : this.step);
      this.emit('bp:beautyves_path_select', { path, source: 'beautyves-concierge' });
      if (options.focus) this.resultQuestion?.querySelector('legend')?.focus?.();
    }

    selectResult(resultKey, options = {}) {
      const result = this.results.find((candidate) => candidate.dataset.resultKey === resultKey);
      if (!result || (this.selectedPath && result.dataset.resultPath !== this.selectedPath)) return;
      this.selectedResultKey = resultKey;
      this.results.forEach((candidate) => { candidate.hidden = candidate !== result; });
      this.updateRoutineState(result);
      if (this.atelier) this.showStep(this.step);
      this.emit('bp:beautyves_result_view', {
        path: result.dataset.resultPath,
        result_key: resultKey,
        source: 'beautyves-concierge',
      });
      this.announce(result.querySelector('h3')?.textContent || '');
      if (options.focus && !this.atelier) {
        result.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        result.querySelector('h3')?.setAttribute('tabindex', '-1');
        result.querySelector('h3')?.focus({ preventScroll: true });
      }
    }

    showStep(step, focus = false) {
      if (!this.atelier) return;
      if (![1, 2, 3].includes(step) || (step > 1 && !this.selectedPath) || (step === 3 && !this.selectedResultKey)) return;
      this.step = step;
      this.root.dataset.bvStep = String(step);
      const targets = [this.pathQuestion, this.resultQuestion, this.root.querySelector('[data-bv-results]')];
      targets.forEach((element, index) => {
        if (!element) return;
        element.hidden = index !== step - 1;
        element.inert = element.hidden;
      });
      this.results.forEach(result => { result.hidden = result.dataset.resultKey !== this.selectedResultKey; });
      const nav = this.root.querySelector('[data-bv-navigation]');
      if (nav) nav.hidden = false;
      this.root.querySelectorAll('[data-bv-go]').forEach(button => {
        const destination = Number(button.dataset.bvGo);
        button.disabled = destination > 1 && !this.selectedPath || destination === 3 && !this.selectedResultKey;
        if (destination === step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      const forward = this.root.querySelector('[data-bv-forward]');
      if (forward) forward.hidden = step === 3;
      const next = this.root.querySelector('[data-bv-next]');
      if (next) next.disabled = step === 1 ? !this.selectedPath : !this.selectedResultKey;
      const nextLabel = this.root.querySelector('[data-bv-next-label]');
      if (nextLabel) nextLabel.textContent = step === 1 ? nextLabel.dataset.worldLabel : nextLabel.dataset.routineLabel;
      const prompt = this.root.querySelector('[data-bv-prompt]');
      if (prompt) prompt.textContent = step === 1 ? prompt.dataset.worldPrompt : prompt.dataset.desirePrompt;
      const worldInput = this.root.querySelector('[data-bp-beautyves-path]:checked');
      const resultInput = this.root.querySelector('[data-bp-beautyves-result-choice]:checked');
      const worldName = worldInput?.nextElementSibling?.querySelector('strong')?.textContent || '';
      const resultName = resultInput?.nextElementSibling?.querySelector('strong')?.textContent || '';
      const recap = this.root.querySelector('[data-bv-recap]');
      if (recap) {
        recap.hidden = step === 1;
        recap.querySelector('[data-bv-recap-value]').textContent = [worldName, ...(step === 3 ? [resultName] : [])].filter(Boolean).join(' / ');
      }
      this.root.dataset.bvReady = 'true';
      if (focus) {
        const heading = targets[step - 1]?.querySelector(step === 3 ? '[data-bp-beautyves-result]:not([hidden]) h3' : 'legend');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
        nav?.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }

    itemFromCard(card) {
      return {
        element: card,
        productHandle: card.dataset.productHandle || '',
        productAvailable: card.dataset.productAvailable === 'true',
        variantId: card.dataset.selectedVariantId || '',
        variantAvailable: card.dataset.selectedVariantAvailable === 'true',
        priceCents: card.dataset.selectedVariantPrice || card.dataset.minimumPrice || '',
      };
    }

    updateRoutineState(result) {
      if (!result) return;
      const items = Array.from(result.querySelectorAll('[data-bp-beautyves-product]')).map((card) => this.itemFromCard(card));
      const total = Core.calculateTotal(items);
      const totalElement = result.querySelector('[data-bp-beautyves-total-value]');
      const prefix = result.querySelector('[data-bp-beautyves-total-prefix]');
      const footerTotal = result.querySelector('[data-bv-checkout-total-value]');
      const footerPrefix = result.querySelector('[data-bv-checkout-total-prefix]');
      const missing = items.find((item) => !Core.variantId(item.variantId));
      const totalLabel = missing ? this.root.dataset.totalFromLabel : this.root.dataset.totalLabel;
      if (total > 0) {
        if (totalElement) totalElement.textContent = this.formatMoney(total);
        if (footerTotal) footerTotal.textContent = this.formatMoney(total);
      }
      if (prefix) prefix.textContent = totalLabel;
      if (footerPrefix) footerPrefix.textContent = totalLabel;
      const hint = result.querySelector('[data-bv-variant-hint]');
      if (hint) hint.hidden = !missing;
      const addAllLabel = result.querySelector('[data-bp-beautyves-add-all] [data-bp-beautyves-button-label]');
      if (addAllLabel) {
        const nextChoice = missing?.element.querySelector('[data-bp-beautyves-variants] legend')?.textContent?.trim();
        addAllLabel.textContent = nextChoice || addAllLabel.dataset.defaultLabel;
      }
    }

    async addItems(cards, button, mode) {
      const result = button.closest('[data-bp-beautyves-result]');
      const items = cards.map((card) => this.itemFromCard(card));
      const validation = Core.validateRoutine(items);
      if (!validation.valid) {
        this.reportValidationError(validation, items, result);
        return;
      }

      const response = await this.gate.run(async () => {
        this.setPending(button, true);
        try {
          const cart = document.querySelector('cart-drawer');
          cart?.setActiveElement?.(button);
          const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
          const request = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: Core.buildCartLines(items),
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const text = await request.text();
          let payload;
          try {
            payload = JSON.parse(text);
          } catch (_error) {
            throw Object.assign(new Error('invalid_response'), { code: 'invalid_response' });
          }

          if (!request.ok || payload.status) {
            throw Object.assign(new Error('cart_error'), {
              code: Core.classifyCartError(request.status, payload),
              payload,
            });
          }

          if (cart && payload.sections) cart.renderContents(payload, true);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'beautyves-concierge',
              productVariantId: items[0].variantId,
              cartData: payload,
            });
          }

          this.emit('bp:beautyves_routine_add', {
            path: result?.dataset.resultPath,
            result_key: result?.dataset.resultKey,
            product_handle: mode === 'single' ? items[0].productHandle : undefined,
            variant_id: mode === 'single' ? items[0].variantId : undefined,
            item_count: items.length,
            source: 'beautyves-concierge',
          });
          this.announce(this.root.dataset.cartSuccess, true);
          return payload;
        } catch (error) {
          const errorCode = error.code || (error instanceof TypeError ? 'network_error' : 'cart_error');
          this.emit('bp:beautyves_routine_add_error', {
            path: result?.dataset.resultPath,
            result_key: result?.dataset.resultKey,
            item_count: items.length,
            source: 'beautyves-concierge',
            error_code: errorCode,
          });
          this.announce(this.messageForError(errorCode), true);
          return { error: true, code: errorCode };
        } finally {
          this.setPending(button, false);
        }
      });

      return response;
    }

    reportValidationError(validation, items, result) {
      const card = items[validation.index]?.element;
      if (validation.code === 'missing_variant') {
        const error = card?.querySelector('[data-bp-beautyves-variant-error]');
        error?.removeAttribute('hidden');
        const firstInput = card?.querySelector('[data-bp-beautyves-variant]:not(:disabled)');
        firstInput?.focus();
      } else {
        card?.querySelector('a, button, input:not(:disabled)')?.focus();
      }
      this.emit('bp:beautyves_routine_add_error', {
        path: result?.dataset.resultPath,
        result_key: result?.dataset.resultKey,
        item_count: items.length,
        source: 'beautyves-concierge',
        error_code: validation.code,
      });
      this.announce(this.messageForError(validation.code), true);
    }

    messageForError(code) {
      if (code === 'missing_variant') return this.root.dataset.variantRequired;
      if (code === 'sold_out') return this.root.dataset.cartSoldOut;
      if (code === 'network_error') return this.root.dataset.cartNetworkError;
      if (code === 'invalid_response') return this.root.dataset.cartInvalidResponse;
      if (code === 'session_error') return this.root.dataset.cartSessionError;
      return this.root.dataset.cartError;
    }

    setPending(button, pending) {
      button.disabled = pending;
      button.setAttribute('aria-busy', String(pending));
      button.querySelector('[data-bp-beautyves-button-label]')?.toggleAttribute('hidden', pending);
      button.querySelector('[data-bp-beautyves-button-pending]')?.toggleAttribute('hidden', !pending);
    }

    formatMoney(cents) {
      const currency = this.root.dataset.currency || 'EUR';
      return new Intl.NumberFormat(document.documentElement.lang || 'it', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    }

    announce(message, visible = false) {
      if (!this.liveRegion || !message) return;
      this.liveRegion.classList.toggle('bv-status', visible);
      this.liveRegion.textContent = '';
      window.cancelAnimationFrame(this.announcementFrame);
      this.announcementFrame = window.requestAnimationFrame(() => { this.liveRegion.textContent = message; });
    }

    emit(name, payload) {
      this.root.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail: Core.filterAnalyticsPayload(payload),
      }));
    }

    handleBlockSelect(blockId) {
      const block = this.root.querySelector(`[data-shopify-editor-block*="${CSS.escape(blockId)}"], [data-result-key][data-block-id="${CSS.escape(blockId)}"]`);
      const result = block?.matches?.('[data-bp-beautyves-result]') ? block : block?.closest?.('[data-bp-beautyves-result]');
      if (!result) return;
      const path = result.dataset.resultPath;
      const pathInput = this.root.querySelector(`[data-bp-beautyves-path][value="${CSS.escape(path)}"]`);
      if (pathInput) pathInput.checked = true;
      this.selectPath(path, { source: 'editor' });
      const resultInput = this.root.querySelector(`[data-bp-beautyves-result-choice][value="${CSS.escape(result.dataset.resultKey)}"]`);
      if (resultInput) resultInput.checked = true;
      this.selectResult(result.dataset.resultKey, { source: 'editor', focus: false });
      this.showStep(3);
      result.classList.add('is-editor-selected');
      result.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }

    handleBlockDeselect() {
      this.root.querySelectorAll('.is-editor-selected').forEach((element) => element.classList.remove('is-editor-selected'));
    }

    destroy() {
      this.abortController.abort();
      window.cancelAnimationFrame(this.announcementFrame);
      this.root.classList.remove('is-enhanced');
      this.results.forEach((result) => {
        result.hidden = false;
        result.classList.remove('is-editor-selected');
      });
      if (this.resultQuestion) this.resultQuestion.hidden = false;
      [this.pathQuestion, this.resultQuestion, this.root.querySelector('[data-bv-results]')].forEach(element => { if (element) { element.hidden = false; element.inert = false; } });
      this.root.querySelectorAll('[data-bv-navigation], [data-bv-forward], [data-bv-recap]').forEach(element => { element.hidden = true; });
      this.root.querySelectorAll('[data-bp-beautyves-result-option]').forEach(element => { element.hidden = false; element.querySelector('input').disabled = false; });
      delete this.root.dataset.bvReady;
      delete this.root.dataset.bvStep;
      instances.delete(this.root);
      instancesBySectionId.delete(this.sectionId);
    }
  }

  const rootsWithin = (container) => {
    const roots = [];
    if (container instanceof Element && container.matches(rootSelector)) roots.push(container);
    container.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    return roots;
  };

  const initialize = (container = document) => {
    rootsWithin(container).forEach((root) => {
      if (instances.has(root)) return;
      const instance = new BeautyVesConcierge(root);
      instances.set(root, instance);
      instancesBySectionId.set(instance.sectionId, instance);
      instance.initialize();
    });
  };

  const destroy = (container) => rootsWithin(container).forEach((root) => instances.get(root)?.destroy());

  const instanceForEvent = (event) => {
    const directRoot = event.target?.closest?.(rootSelector) || event.target?.querySelector?.(rootSelector);
    if (directRoot && instances.has(directRoot)) return instances.get(directRoot);
    return instancesBySectionId.get(event.detail?.sectionId);
  };

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => instanceForEvent(event)?.handleBlockSelect(event.detail?.blockId));
  document.addEventListener('shopify:block:deselect', (event) => instanceForEvent(event)?.handleBlockDeselect(event.detail?.blockId));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  window.BPBeautyVesConcierge = Object.freeze({ initialize, destroy });
})();
